import type { ReportSection } from '@neo/blocks/sidebar/app-sidebar-reports'
import { phoneNumbersRoleAccess } from 'v2/api-console/numbers/phone-numbers-tabs'
import { getComplianceTabVisibility } from 'v2/pages/10dlc/compliance-tabs'
import { hasBalanceAlerts } from 'v2/pages/alerting/alerting-tabs'
import { useAccountDetailsQuery } from 'v2/pages/billing/queries'
import { useSenderIdAccessQuery } from 'v2/pages/settings/phone-numbers/sender-id-queries'
import { useIndiaComplianceFirstRun } from 'v2/pages/settings/phone-numbers/use-india-compliance-first-run'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useSandboxSetupVisible } from 'v2/pages/settings/sandbox-numbers/use-sandbox-setup-visible'
import { useWhatsAppTemplatesConfigQuery } from 'v2/pages/settings/whatsapp/templates/queries'
import { useChannelLocks } from 'v2/shared/hooks/use-channel-locks'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { isRouteAllowed, resolveWorkspaceNav } from 'v2/shared/navigation/workspace-nav-utils'
import type { IRoute } from 'v2/types'
import { getLocalAuth, getSessionRoles } from 'v2/utils'

/**
 * The workspace sidebar for this session, gated exactly as the pages behind it are. The locks are
 * final on first render: OnboardingGate holds the layout until the profile they come from loads.
 */
export const useWorkspaceNav = (routes: IRoute[], reportSections: ReportSection[]) => {
  const { data: profile } = useProfileQuery()
  const { isEnterpriseLocked } = useUserPlan()
  const { isWhatsappLocked, isVerifyLocked, isVerifyAvailable, isChannelLocksLoaded } =
    useChannelLocks()
  // The Phone Numbers page's own two access checks, so Sender IDs and Verified destinations
  // show exactly when their tabs do.
  const showSandboxTab = useSandboxSetupVisible()
  const { hasSenderIdAccess } = useSenderIdAccessQuery()
  // The Alerts page's own account query: Balance alerts are prepaid-only.
  const { data: accountDetails } = useAccountDetailsQuery()
  const isPostpaid = accountDetails?.account.postpaid
  // The WhatsApp page's own query, so Templates shows exactly when its tab does; nothing is
  // fetched for a session that can't open WhatsApp.
  const { data: whatsappAccounts } = useWhatsAppTemplatesConfigQuery(undefined, {
    enabled: isRouteAllowed(routes, '/whatsapp') && isChannelLocksLoaded && !isWhatsappLocked,
  })
  // Read-only: the Home and Phone Numbers pages fetch (and poll) the KYC gate; the sidebar follows
  // their cache instead of polling from every page. No cache reads as not gated, as the page does.
  const { isKycRequired } = useIndiaComplianceFirstRun(false)

  const { permissions } = usePermissions()
  const region = getLocalAuth().region
  const roles = getSessionRoles()
  const org = profile?.organizations?.[0]

  return resolveWorkspaceNav(routes, reportSections, {
    phoneNumbersAccess: {
      ...phoneNumbersRoleAccess({ region, roles, permissionsMap: permissions }),
      showSandboxTab,
      hasSenderIdAccess,
    },
    isIndiaKycRequired: isKycRequired,
    compliance: getComplianceTabVisibility(org, isEnterpriseLocked),
    hasWhatsAppTemplates: !!whatsappAccounts?.length,
    isVerifyAvailable,
    locks: { isEnterpriseLocked, isWhatsappLocked, isVerifyLocked },
    hasBalanceAlerts: hasBalanceAlerts(isPostpaid),
  })
}
